import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAlertHistory,
  getAlertHistoryDetail,
  getActiveAlerts,
  getIncidentDetailExportJson,
  getDeliveryReport,
  getAudienceDetail,
  endAlert,
} from '../../services/alerts.service';
import type {
  AlertHistoryFilters,
  ActiveAlertFilters,
  IncidentDetailPayload,
  EndAlertPayload,
} from '../../services/alerts.service';

// =============================================================================
// Query Key Factory
// =============================================================================

export const alertHistoryKeys = {
  all: ['alertHistory'] as const,
  lists: () => [...alertHistoryKeys.all, 'list'] as const,
  list: (filters: AlertHistoryFilters) =>
    [...alertHistoryKeys.lists(), filters] as const,
  details: () => [...alertHistoryKeys.all, 'detail'] as const,
  detail: (id: number) => [...alertHistoryKeys.details(), id] as const,
};

export const activeAlertKeys = {
  all: ['activeAlerts'] as const,
  lists: () => [...activeAlertKeys.all, 'list'] as const,
  list: (filters: ActiveAlertFilters) =>
    [...activeAlertKeys.lists(), filters] as const,
};

// =============================================================================
// Alert History Hook (all past + current alerts/drills — Events page)
// =============================================================================

export function useAlertHistory(filters: AlertHistoryFilters = {}) {
  return useQuery({
    queryKey: alertHistoryKeys.list(filters),
    queryFn: () => getAlertHistory(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useAlertHistoryDetail(id: number) {
  return useQuery({
    queryKey: alertHistoryKeys.detail(id),
    queryFn: () => getAlertHistoryDetail(id),
    enabled: !!id,
  });
}

// =============================================================================
// Alert History — Drawer hooks (Events tab + Delivery tab)
// Mirror AIM's Settings > History > View drawer data layer; reuse the same
// backend endpoints. Kept SMS-local rather than shared-lib for now per the
// in-flight ADR on shared http-client.
// =============================================================================

export const incidentDetailKeys = {
  all: ['incidentDetail'] as const,
  byPayload: (payload: IncidentDetailPayload) =>
    [...incidentDetailKeys.all, payload] as const,
};

export const deliveryReportKeys = {
  all: ['deliveryReport'] as const,
  detail: (id: number) => [...deliveryReportKeys.all, id] as const,
  audience: (id: number) =>
    [...deliveryReportKeys.all, id, 'audience'] as const,
};

/**
 * Paginated incident-detail fetch for the Events tab.
 * Caller drives offset; this hook returns the current page's response and
 * lets the caller append/replace its own list state (the AIM pattern, kept so
 * the consumer can react to "offset === 0 → replace, else → append").
 */
export function useAlertHistoryDrawerData(
  payload: IncidentDetailPayload,
  enabled: boolean,
) {
  return useQuery({
    queryKey: incidentDetailKeys.byPayload(payload),
    queryFn: () => getIncidentDetailExportJson(payload),
    enabled: enabled && !!payload.id,
  });
}

export function useDeliveryReport(alertId: number | null, enabled = true) {
  return useQuery({
    queryKey: deliveryReportKeys.detail(alertId ?? 0),
    queryFn: () => {
      if (!alertId) {
        throw new Error('alertId required');
      }
      return getDeliveryReport(alertId);
    },
    enabled: enabled && !!alertId,
  });
}

export function useAudienceDetail(
  alertId: number | null,
  enabled: boolean,
  available: boolean,
) {
  return useQuery({
    queryKey: deliveryReportKeys.audience(alertId ?? 0),
    queryFn: () => {
      if (!alertId) {
        throw new Error('alertId required');
      }
      return getAudienceDetail(alertId);
    },
    enabled: enabled && !!alertId && available,
  });
}

// =============================================================================
// End an active alert / drill (DELETE /alert/{id}/)
// =============================================================================

export function useEndAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload?: EndAlertPayload }) =>
      endAlert(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertHistoryKeys.all });
      queryClient.invalidateQueries({ queryKey: activeAlertKeys.all });
    },
  });
}

// =============================================================================
// Active Alerts Hook (currently in-progress only — Dashboard stat cards)
// =============================================================================

export function useActiveAlerts(filters: ActiveAlertFilters = {}) {
  return useQuery({
    queryKey: activeAlertKeys.list(filters),
    queryFn: () => getActiveAlerts(filters),
    placeholderData: (previousData) => previousData,
  });
}
